/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-unused-vars */
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchPieChartData } from "../../redux/Slices/dashboardSlice";
import {
  PieChart,
  Pie,
  Cell,
  Label,
  Legend,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import Loader from "../../components/Common/Loader";
import { normalizeDateForAPI } from "../../utils/Utils";
import appInsights from "../../AppInsights";

const CustomLabel = ({ viewBox, totalLeadCount }) => {
  const { cx, cy } = viewBox;
  return (
    <>
      <text x={cx} y={cy - 15} className="piechart-total-label ">
        Total New Leads
      </text>
      <text x={cx} y={cy + 10} className="piechart-total-value ">
        {totalLeadCount}
      </text>
    </>
  );
};

const PieChartt = ({ startDate, endDate }) => {
  const dispatch = useDispatch();
  const { profileData } = useSelector((state) => state.profile);
  const { pieChartData, loading, error } = useSelector(
    (state) => state.dashboard
  );

  const coachId = profileData?.coachId;
  const wonLeadCount = pieChartData?.wonLeadCount || 0;
  const totalLeadCount = pieChartData?.totalLeadCount || 0;
  const unconvertedLeads = Math.max(totalLeadCount - wonLeadCount, 0);

  // Track component load
  useEffect(() => {
    const openTime = new Date().toLocaleString();
    appInsights.trackEvent({
      name: "PieChart_Component_Loaded",
      properties: {
        openedAt: openTime,
        coachId: coachId,
        startDate: startDate?.toISOString(),
        endDate: endDate?.toISOString(),
        component: "Lead_Activity_Pie_Chart",
      },
    });
  }, []);

  useEffect(() => {
    if (coachId) {
      // Track pie chart data fetch attempt
      appInsights.trackEvent({
        name: "PieChart_Data_Fetch_Attempt",
        properties: {
          coachId: coachId,
          startDate: startDate ? normalizeDateForAPI(startDate) : null,
          endDate: endDate ? normalizeDateForAPI(endDate) : null,
          dateRangeProvided: !!(startDate && endDate),
        },
      });

      dispatch(
        fetchPieChartData({
          coachId,
          startDate: startDate ? normalizeDateForAPI(startDate) : null,
          endDate: endDate ? normalizeDateForAPI(endDate) : null,
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, startDate, endDate, coachId]);

  // Track successful data load
  useEffect(() => {
    if (!loading && pieChartData && coachId) {
      appInsights.trackEvent({
        name: "PieChart_Data_Loaded",
        properties: {
          coachId: coachId,
          totalLeadCount: pieChartData.totalLeadCount,
          wonLeadCount: pieChartData.wonLeadCount,
          conversionRate: pieChartData.conversionRate,
          unconvertedLeads: unconvertedLeads,
          startDate: startDate?.toISOString(),
          endDate: endDate?.toISOString(),
          hasData: pieChartData.totalLeadCount > 0,
        },
      });
    }
  }, [loading, pieChartData, coachId, startDate, endDate, unconvertedLeads]);

  // Track date range changes
  useEffect(() => {
    if (coachId && (startDate || endDate)) {
      appInsights.trackEvent({
        name: "PieChart_Date_Range_Changed",
        properties: {
          coachId: coachId,
          startDate: startDate?.toISOString(),
          endDate: endDate?.toISOString(),
          hasStartDate: !!startDate,
          hasEndDate: !!endDate,
        },
      });
    }
  }, [startDate, endDate, coachId]);

  // Track loading state
  useEffect(() => {
    if (loading) {
      appInsights.trackEvent({
        name: "PieChart_Loading_State",
        properties: {
          coachId: coachId,
          component: "Lead_Activity_Pie_Chart",
        },
      });
    }
  }, [loading, coachId]);

  // Track errors
  useEffect(() => {
    if (error) {
      appInsights.trackException(
        new Error("PieChart_Data_Fetch_Error"),
        "PieChart_Data_Fetch_Error",
        {
          coachId: coachId,
          error: error,
          startDate: startDate?.toISOString(),
          endDate: endDate?.toISOString(),
          context: "pie_chart_data_fetch",
        }
      );
    }
  }, [error, coachId, startDate, endDate]);

  // Track chart rendering
  useEffect(() => {
    if (!loading && pieChartData) {
      const hasRealData = wonLeadCount > 0 || unconvertedLeads > 0;
      
      appInsights.trackEvent({
        name: "PieChart_Rendered",
        properties: {
          coachId: coachId,
          dataPoints: chartData.length,
          hasRealData: hasRealData,
          isPlaceholderData: !hasRealData,
          conversionRate: formattedConversionRate,
          totalLeads: totalLeadCount,
          chartType: "pie",
        },
      });
    }
  }, [loading, pieChartData, coachId, wonLeadCount, unconvertedLeads, totalLeadCount]);

  // Track chart interactions
  const handleChartInteraction = (data, index) => {
    if (data && data.activePayload) {
      const interactedData = data.activePayload[0]?.payload;
      if (interactedData && !interactedData.isPlaceholder) {
        appInsights.trackEvent({
          name: "PieChart_Segment_Interacted",
          properties: {
            coachId: coachId,
            segmentName: interactedData.name,
            segmentValue: interactedData.value,
            segmentPercentage: totalLeadCount > 0 ? 
              ((interactedData.value / totalLeadCount) * 100).toFixed(1) + '%' : '0%',
            interactionType: "hover_click",
            totalLeads: totalLeadCount,
          },
        });
      }
    }
  };

  // Track tooltip interactions
  const handleTooltipInteraction = (data) => {
    if (data && data.payload && data.payload[0]) {
      const tooltipData = data.payload[0].payload;
      if (tooltipData && !tooltipData.isPlaceholder) {
        appInsights.trackEvent({
          name: "PieChart_Tooltip_Viewed",
          properties: {
            coachId: coachId,
            segmentName: tooltipData.name,
            segmentValue: tooltipData.value,
            conversionRate: formattedConversionRate,
          },
        });
      }
    }
  };

  if (loading) return <Loader />;
  if (error) return <p>{error}</p>;

  const formattedConversionRate = pieChartData?.conversionRate
    ? parseFloat(pieChartData.conversionRate).toFixed(2)
    : "0.00";

  const chartData =
    wonLeadCount === 0 && unconvertedLeads === 0
      ? [{ name: "No Data", value: 1, isPlaceholder: true }]
      : [
          { name: "Won Leads", value: wonLeadCount },
          { name: "Unconverted Leads", value: unconvertedLeads },
        ];

  return (
    <div className="piechart-container ">
      <div className="website-hits-header ">
        {/* Title Section */}
        <div className="website-hits-title ">
          <h1>Lead Activity</h1>
        </div>

        {/* Total Hits Section */}
        <div className="website-hits-total">
          <h1>Lead Conversion Rate</h1>
          <p className="total-hits-count">{formattedConversionRate}%</p>
        </div>
      </div>
      <ResponsiveContainer width="100%" height={165}>
        <PieChart>
          <Pie
            data={chartData}
            dataKey="value"
            outerRadius={80}
            innerRadius={60}
            onClick={handleChartInteraction}
            onMouseEnter={handleChartInteraction}
          >
            {chartData.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={
                  entry.isPlaceholder
                    ? "#D3D3D3"
                    : index === 0
                    ? "#383D8D"
                    : "#A42066"
                }
              />
            ))}
            <Label
              content={
                <CustomLabel
                  totalLeadCount={pieChartData?.totalLeadCount || 0}
                />
              }
              position="center"
            />
          </Pie>

          <Tooltip
            formatter={(value, name) => [`${value}`, `${name}`]}
            contentClassName="piechart-tooltip"
            wrapperStyle={{ zIndex: 1000 }}
          />
          <Legend
            layout="vertical"
            align="right"
            verticalAlign="middle"
            wrapperStyle={{
              marginTop: "20px",
              marginRight: "37px",
            }}
          />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
};

export default PieChartt;